import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'

import ModalIllustrativeIcon from '@design-system/modalillustrativeicon'

import { useGeo } from 'context/GeoContext'
import { useLocale } from 'context/LocaleContext'
import { useModalGeoContextPropsStore } from 'store/useModalGeoContextProps'

import { useGtmTrack } from 'modules/gtm'

import { buildSameSiteDestination } from 'utils/geo/buildSameSiteDestination'

import { COUNTRY_NAME_BY_LOCALE } from './countryNames'

const GEO_MODAL_GTM_SECTION = 'geo mismatch modal'

export default function ModalGeoContext() {
  const { asPath } = useRouter()
  const {
    userCountry,
    currentLocale,
    isMismatch,
    isCrossSite,
    isUnsupportedCountry,
    suggestedLocale,
    shouldShowModal,
    crossSiteHomeUrl: crossSiteHomeUrlFromEnv
  } = useGeo()
  const { brand } = useLocale()
  const { dataLayerPush } = useGtmTrack()

  const showModalFromCms = useModalGeoContextPropsStore.use.showModal()
  const sameSiteTexts = useModalGeoContextPropsStore.use.sameSiteTexts()
  const crossSiteTexts = useModalGeoContextPropsStore.use.crossSiteTexts()
  const notSupportedCountryTexts =
    useModalGeoContextPropsStore.use.notSupportedCountryTexts()
  const crossSiteHomeUrlFromCms =
    useModalGeoContextPropsStore.use.crossSiteHomeUrl()

  const [isOpen, setIsOpen] = useState(false)

  // Open automatically when a geo scenario is detected (once per page load)
  useEffect(() => {
    if (shouldShowModal) setIsOpen(true)
  }, [shouldShowModal])

  // CTA destination: cross-site -> other site home; same-site -> suggested locale path
  // preserving the current page path with the locale prefix swapped (/co/home -> /ec/home)
  const ctaHref = useMemo(() => {
    if (isCrossSite) {
      const crossSiteUrl = crossSiteHomeUrlFromCms || crossSiteHomeUrlFromEnv
      // The other site's home URL may contain a {country} placeholder
      // (e.g. https://www.directvgo.com/{country}/home) — resolve it to the
      // visitor's country so a CO user on SKY+ lands on /co/home
      return crossSiteUrl
        ? crossSiteUrl.replace('{country}', suggestedLocale ?? '')
        : null
    }
    if (isMismatch && suggestedLocale) {
      return buildSameSiteDestination(asPath, suggestedLocale)
    }
    return null
  }, [
    isCrossSite,
    isMismatch,
    suggestedLocale,
    asPath,
    crossSiteHomeUrlFromCms,
    crossSiteHomeUrlFromEnv
  ])

  // Replace placeholders in labels:
  // - {country}: country of the CURRENT locale (where the user is browsing)
  // - {destinationCountry}: country of the suggested locale (where the CTA leads)
  const currentCountryName = COUNTRY_NAME_BY_LOCALE[currentLocale ?? ''] ?? ''
  const destinationCountryName =
    COUNTRY_NAME_BY_LOCALE[suggestedLocale ?? ''] ?? ''

  const resolveLabel = (label: string) =>
    label
      .replace('{country}', currentCountryName)
      .replace('{destinationCountry}', destinationCountryName)

  const label = {
    title: isUnsupportedCountry
      ? notSupportedCountryTexts.notSupportedCountryTitle
      : isCrossSite
      ? crossSiteTexts.crossSiteTitle
      : sameSiteTexts.sameSiteTitle,
    description: isUnsupportedCountry
      ? notSupportedCountryTexts.notSupportedCountryDescription
      : isCrossSite
      ? crossSiteTexts.crossSiteDescription
      : sameSiteTexts.sameSiteDescription,
    primaryButtonLabel: isUnsupportedCountry
      ? notSupportedCountryTexts.notSupportedCountryPrimaryButtonLabel
      : isCrossSite
      ? crossSiteTexts.crossSitePrimaryButtonLabel
      : sameSiteTexts.sameSitePrimaryButtonLabel,
    secondaryButtonLabel: isUnsupportedCountry
      ? notSupportedCountryTexts.notSupportedCountrySecondaryButtonLabel
      : isCrossSite
      ? crossSiteTexts.crossSiteSecondaryButtonLabel
      : sameSiteTexts.sameSiteSecondaryButtonLabel
  }

  const handlePrimaryButton = () => {
    // Unsupported country: no redirect target defined yet (pending UX final design)
    if (isUnsupportedCountry) {
      // eslint-disable-next-line no-console
      console.log(
        '[ModalGeoContext] primary button clicked (unsupported country)',
        { userCountry }
      )
      return
    }
    dataLayerPush({
      event: 'cta_geo_redirect',
      section: GEO_MODAL_GTM_SECTION,
      element: ctaHref ?? '',
      user_country: userCountry ?? '',
      action: 'click'
    })
    if (ctaHref) window.location.href = ctaHref
  }

  const handleSecondaryButton = () => {
    // Unsupported country: no secondary action defined yet (pending UX final design)
    if (isUnsupportedCountry) {
      // eslint-disable-next-line no-console
      console.log(
        '[ModalGeoContext] secondary button clicked (unsupported country)',
        { userCountry }
      )
      return
    }
    dataLayerPush({
      event: 'cta_geo_continue',
      section: GEO_MODAL_GTM_SECTION,
      element: 'continuar',
      user_country: userCountry ?? '',
      action: 'click'
    })
    setIsOpen(false)
  }

  if (!showModalFromCms || !shouldShowModal || !label.title) {
    return null
  }

  return (
    <ModalIllustrativeIcon
      isOpen={isOpen}
      onClose={handleSecondaryButton}
      onClickPrimaryButton={handlePrimaryButton}
      onClickSecondaryButton={handleSecondaryButton}
      secondaryButton={{ appearance: 'outline' }}
      bgSecondary={true}
      project={brand}
      iconName="international"
      title={resolveLabel(label.title)}
      description={resolveLabel(label.description)}
      primaryButtonLabel={resolveLabel(label.primaryButtonLabel)}
      secondaryButtonLabel={resolveLabel(label.secondaryButtonLabel)}
      showPrimaryButton={Boolean(label.primaryButtonLabel)}
      showSecondaryButton={Boolean(label.secondaryButtonLabel)}
      disableClose={isUnsupportedCountry}
    />
  )
}
